import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, StoreType } from 'store';
import { fetchLanguages, fetchTranslations } from './services/api';
import { Languages, LanguageTranslations, SupportedLanguages, Translations } from './types';
import { trackLanguageToggle } from '../../scenes/PreferencesCenter/services/userActionEvents';
import {
  LOCALIZATION_INITIALIZATION_PREFIX,
  LOCALIZATION_LOAD_LANGUAGES_PREFIX,
  LOCALIZATION_LOAD_TRANSLATION_PREFIX,
  LOCALIZATION_SWITCH_LANGUAGE_PREFIX,
  LOCALIZATION_SLICE_NAME,
} from './constants';

interface LocalizationState {
  language: string;
  fallbackLanguage: string;
  systemLanguage: string;
  userLanguage: string | undefined;
  supportedLanguages: SupportedLanguages;
  translations: LanguageTranslations;
}

const extractSystemLanguage = (): string => window.navigator.languages[0].split('-')[0];
const FALLBACK_LANGUAGE = 'en';

const initialState: LocalizationState = {
  language: FALLBACK_LANGUAGE,
  fallbackLanguage: FALLBACK_LANGUAGE,
  systemLanguage: extractSystemLanguage(),
  userLanguage: undefined,
  supportedLanguages: { en: 'English' },
  translations: {},
};

export const loadTranslation = createAsyncThunk<Translations, string, { state: StoreType }>(
  LOCALIZATION_LOAD_TRANSLATION_PREFIX,
  async (language: string) => {
    return await fetchTranslations(language);
  },
  {
    condition: (language: string, { getState }): boolean => getState().localization.translations[language] == null,
  },
);

export const loadLanguages = createAsyncThunk<Languages, null, { state: StoreType }>(
  LOCALIZATION_LOAD_LANGUAGES_PREFIX,
  async () => {
    return await fetchLanguages();
  },
  {
    //condition: (language: string, { getState }): boolean => getState().localization.translations[language] == null,
  },
);

export const initLocalization = createAsyncThunk<void, void, { dispatch: AppDispatch }>(
  LOCALIZATION_INITIALIZATION_PREFIX,
  async (_, { dispatch }) => {
    await dispatch(loadTranslation(FALLBACK_LANGUAGE));
    await dispatch(loadLanguages(null));
  },
);

export const switchLanguage = createAsyncThunk<string, string, { dispatch: AppDispatch }>(
  LOCALIZATION_SWITCH_LANGUAGE_PREFIX,
  async (language: string, { dispatch }) => {
    await dispatch(loadTranslation(language));
    trackLanguageToggle({ lang: language });
    return language;
  },
);
const localizationSlice = createSlice({
  name: LOCALIZATION_SLICE_NAME,
  initialState,
  reducers: {
    changeUserLanguage: (state, action: PayloadAction<string>) => {
      state.userLanguage = action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<LocalizationState>) => {
    builder.addCase(loadTranslation.fulfilled, (state, { payload, meta }) => {
      state.translations = {
        ...state.translations,
        [meta.arg]: payload,
      };
    });
    builder.addCase(loadLanguages.fulfilled, (state, { payload }) => {
      state.supportedLanguages = payload;
    });
    builder.addCase(switchLanguage.fulfilled, (state, { payload }) => {
      state.language = payload;
    });
  },
});

const { changeUserLanguage } = localizationSlice.actions;

export { changeUserLanguage };

export default localizationSlice.reducer;
